import { cva, type VariantProps } from 'class-variance-authority';
import { ReactElement } from 'react';

import { twMerge } from 'tailwind-merge';

const button = cva(
  [
    'justify-center',
    'inline-flex',
    'items-center',
    'rounded-lg',
    'text-center',
    'border',
    'border-floxBlue',
    'transition-colors',
    'delay-50',
    'font-inter',
  ],
  {
    variants: {
      intent: {
        primary: [
          'bg-primary',
          'text-white',
          'border-0',
          'border-radius-2',
          'hover:enabled:bg-blue-800',
        ],
        secondary: [
          'bg-transparent',
          'text-primary',
          'border-primary',
          'hover:enabled:bg-blue-50',
        ],
        tertiary: [
          'bg-white',
          'text-primary',
          'border-0',
          'hover:enabled:bg-blue-50',
        ],
        success: [
          'bg-success',
          'text-white',
          'border-0',
          'border-radius-2',
          'hover:enabled:bg-green-800',
        ],
        warning: [
          'bg-warning',
          'text-white',
          'border-0',
          'border-radius-2',
          'hover:enabled:bg-yellow-700',
        ],
        danger: [
          'bg-danger',
          'text-white',
          'border-0',
          'border-radius-2',
          'hover:enabled:bg-red-700',
        ],
      },
      size: {
        xs: ['min-w-16', 'h-full', 'min-h-8', 'text-xs', 'py-1', 'px-3'],
        sm: ['min-w-20', 'h-full', 'min-h-10', 'text-sm', 'py-1.5', 'px-4'],
        lg: ['min-w-32', 'h-full', 'min-h-12', 'text-lg', 'py-2.5', 'px-6'],
        xl: ['min-w-64', 'h-full', 'min-h-14', 'text-lg', 'py-3', 'px-8'],
        iconOnly: [
          'min-w-14',
          'h-full',
          'min-h-12',
          'text-lg',
          'py-2.5',
          'px-2',
        ],
      },
      disabled: {
        true: [
          'bg-gray-300',
          'text-gray-500',
          'cursor-not-allowed',
          'border-gray-300',
        ],
        false: [],
      },
    },
    defaultVariants: {
      intent: 'primary',
      size: 'lg',
      disabled: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  label: string;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
  disabled?: boolean;
}

export function Button({
  label,
  startIcon,
  endIcon,
  className,
  intent,
  size,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      type='button'
      className={twMerge(button({ intent, size, disabled }), className)}
      disabled={disabled}
      {...props}
    >
      {startIcon && (
        <span aria-hidden='true' className={size !== 'iconOnly' ? 'mr-2' : ''}>
          {startIcon}
        </span>
      )}
      {label}
      {endIcon && (
        <span aria-hidden='true' className='ml-2'>
          {endIcon}
        </span>
      )}
    </button>
  );
}
